import React, { FC, memo, useEffect } from 'react'
import s from './RestaurantFilters.module.sass'
import { globalStore, useAppDispatch, useAppSelector } from '@app/model/store'
import { applyFilterAndUpdate, applyFilters } from '@features/Restaurant/RestaurantFilters/model/actions'
import { FilterListTemplate } from '@features/Restaurant/RestaurantFilters/ui/FilterListTemplate'
import { FilterOnceTemplate } from '@features/Restaurant/RestaurantFilters/ui/FilterOnceTemplate'
import {
  TypeFilterByKeyListCheckboxes,
  TypeFiltersByKeyAll,
  TypeFiltersByKeyListButtons,
  TypeFiltersByKeyOnce,
  TypeOutput,
} from '@features/Restaurant/RestaurantFilters/model/types'
import { ErrorBoundary } from '@shared/lib/components/ErrorBoundary'
import { FilterButtonsTemplate } from '@features/Restaurant/RestaurantFilters/ui/FilterButtonsTemplate'
import { RestaurantFiltersSkeleton } from '@features/Restaurant/RestaurantFilters/index'
import FilterMap from './ui/FilterMap/FilterMap'
import { useFilters } from '@features/Restaurant/RestaurantFilters/helpers/helper'

const IS_PROD = typeof window !== 'undefined' && window.document.location.host === 'www.afisha.ru'

type TypeFilters = TypeFiltersByKeyAll

interface Props {
  preloadingResults?: boolean
  handleLoading?: (value: boolean) => void
  handleOutputData?: (data: TypeOutput) => void
  selected: string[] // slugs
  className?: string
  hideDivider?: boolean
  filters?: TypeFilters[] | 'all'
  reloadMap?: boolean
}

const RestaurantFilters: FC<Props> = ({
  preloadingResults = true,
  handleLoading,
  handleOutputData,
  selected,
  hideDivider,
  className = '',
  filters = 'all',
  reloadMap,
}) => {
  const storeCityCoordinates = useAppSelector((store) => store.global.cityRead?.coordinates)
  const [stateFilters] = useFilters(selected, preloadingResults)
  const dispatch = useAppDispatch()

  useEffect(() => {
    handleLoading && handleLoading(stateFilters.output.loading)
  }, [stateFilters.output.loading])

  const handleSendOutputData = async () => {
    if (!stateFilters.ready) return
    await dispatch(reloadMap ? applyFilterAndUpdate() : applyFilters())
    // @ts-ignore
    handleOutputData && handleOutputData(globalStore.getState().features.restaurant_filters.output)
  }

  if (!stateFilters.ready || !storeCityCoordinates) {
    return <RestaurantFiltersSkeleton className={className} />
  }

  const isShowFilterMap = storeCityCoordinates && (filters == 'all' || filters.includes('map'))

  return (
    <ErrorBoundary>
      <div className={`${s.wrapper} ${className}`} data-is-visible={stateFilters.visible}>
        {isShowFilterMap && <FilterMap />}

        {(filters === 'all' ? Object.keys(stateFilters.filters) : filters).map((typeFilter: TypeFiltersByKeyAll) => {
          const filterTypeListTemplate: TypeFilterByKeyListCheckboxes[] = ['establishment', 'kitchen', 'metro']
          const filterTypeButtonsTemplate: TypeFiltersByKeyListButtons[] = ['price', 'rating']
          const filterTypeOnceTemplate: TypeFiltersByKeyOnce[] = ['afishaSelected', 'hasBooking', 'openNow']

          if (!stateFilters.filters[typeFilter].visible) return null
          if (filters !== 'all' && !filters.includes(typeFilter)) return null

          if (filterTypeOnceTemplate.includes(typeFilter as TypeFiltersByKeyOnce)) {
            return (
              <React.Fragment key={`${typeFilter}_wrap`}>
                <FilterOnceTemplate
                  typeFilter={typeFilter as TypeFiltersByKeyOnce}
                  handleChange={handleSendOutputData}
                />

                {!hideDivider && typeFilter === 'openNow' && <div className={s.divider}></div>}
              </React.Fragment>
            )
          }

          if (filterTypeListTemplate.includes(typeFilter as TypeFilterByKeyListCheckboxes)) {
            return (
              <React.Fragment key={`${typeFilter}_wrap`}>
                <FilterListTemplate
                  typeFilter={typeFilter as TypeFilterByKeyListCheckboxes}
                  handleSave={handleSendOutputData}
                  handleRemove={handleSendOutputData}
                />
              </React.Fragment>
            )
          }

          if (filterTypeButtonsTemplate.includes(typeFilter as TypeFiltersByKeyListButtons)) {
            return (
              <React.Fragment key={`${typeFilter}_wrap`}>
                <FilterButtonsTemplate
                  typeFilter={typeFilter as TypeFiltersByKeyListButtons}
                  handleSave={handleSendOutputData}
                  handleRemove={handleSendOutputData}
                />
              </React.Fragment>
            )
          }
        })}
      </div>
    </ErrorBoundary>
  )
}

export default memo(RestaurantFilters)
